import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import KioskHeader from '../../components/KioskHeader/KioskHeader'
import useKioskAutoHome from '../../hooks/useKioskAutoHome'
import { useLang } from '../../context/LangContext'
import {
  Laptop,
  Buildings,
  UsersThree,
  CreditCard,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Ticket,
  Info,
  MapPin,
  Camera,
  WarningCircle,
  UserFocus,
  X,
} from '@phosphor-icons/react'

const CATEGORIES = [
  {
    key: 'IT Support',
    en: 'IT Support',
    bm: 'Sokongan IT',
    descEn: 'System, login, device or software issue',
    descBm: 'Isu sistem, log masuk, peranti atau perisian',
    icon: Laptop,
  },
  {
    key: 'Facilities',
    en: 'Facilities',
    bm: 'Fasiliti',
    descEn: 'Building, classroom, room or maintenance issue',
    descBm: 'Isu bangunan, bilik, kelas atau penyelenggaraan',
    icon: Buildings,
  },
  {
    key: 'HR',
    en: 'HR',
    bm: 'Sumber Manusia',
    descEn: 'Staff, access, request or admin support',
    descBm: 'Staf, akses, permohonan atau sokongan pentadbiran',
    icon: UsersThree,
  },
  {
    key: 'Finance',
    en: 'Finance',
    bm: 'Kewangan',
    descEn: 'Payment, billing, claim or invoice issue',
    descBm: 'Isu bayaran, bil, tuntutan atau invois',
    icon: CreditCard,
  },
]

const PRIORITIES = [
  { key: 'Low', en: 'Low', bm: 'Rendah' },
  { key: 'Medium', en: 'Medium', bm: 'Sederhana' },
  { key: 'High', en: 'High', bm: 'Tinggi' },
  { key: 'Critical', en: 'Critical', bm: 'Kritikal' },
]

const AREAS = ['Kampung Kedup', 'Serian', 'Tebedu', 'Mongkos', 'Other']

const CONTENT = {
  en: {
    pageTitle: 'Submit Ticket',
    pageSubtitle: 'Report an issue to the support team',
    eyebrow: 'Support request',
    heroTitle: 'Submit Ticket',
    heroSubtitle: 'Create a support request with guided steps and clear information.',

    stepPrefix: 'Step',
    stepOf: 'of',
    step1: 'Category',
    step2: 'Details',
    step3: 'Visitor Photo',
    step4: 'Review',

    selectCategory: 'Select ticket category',
    selectCategoryDesc: 'Choose the closest category so we can assign it to the right team.',

    detailsTitle: 'Describe your issue',
    detailsDesc: 'Provide enough details so the support team can understand the issue.',

    visitorPhotoTitle: 'Visitor photo',
    visitorPhotoDesc:
      'Capture the visitor’s photo using the kiosk camera. A photo is required before proceeding to review. File upload is disabled for public kiosk safety.',

    reviewTitle: 'Review ticket',
    reviewDesc: 'Please check the details before submitting your ticket.',

    name: 'Name',
    phone: 'Phone Number',
    area: 'Area',
    otherArea: 'Other Area',
    selectArea: 'Select area',
    subject: 'Subject',
    priority: 'Priority',
    description: 'Description',
    category: 'Category',

    namePlaceholder: 'Example: Martin',
    phonePlaceholder: '161234567',
    otherAreaPlaceholder: 'Please enter your area',
    subjectPlaceholder: 'Example: Air conditioner not working',
    descriptionPlaceholder:
      'Please describe what happened, when it happened, and any important details.',

    continue: 'Continue',
    back: 'Back',
    review: 'Review',
    submit: 'Submit Ticket',

    openCamera: 'Open Camera',
    capturePhoto: 'Capture Photo',
    retakePhoto: 'Retake Photo',
    capturingIn: 'Capturing in',
    getReady: 'Get ready',
    cameraPreview: 'Camera preview',
    cameraPreviewDesc: 'The kiosk will open the camera directly in this box.',
    cameraNotSupported: 'Camera is not supported on this browser or device.',
    cameraPermission: 'Unable to open the camera. Please check camera permission.',

    visitorPhoto: 'Visitor Photo',
    required: 'Required',
    captured: 'Captured',
    notCaptured: 'Not captured',
    notSelected: 'Not selected',
    notEntered: 'Not entered',

    tips: 'Tips',
    ticketGuide: 'Ticket Guide',
    ticketGuideDesc:
      'Select the correct category and provide clear details to help the team respond faster.',
    currentSelection: 'Current Selection',
    tipLocation: 'Include location if relevant.',
    tipPhoto: 'Capture visitor photo for easier follow-up.',
    tipNoUpload: 'File upload is disabled on public kiosk.',

    phoneRequired: 'Phone number is required',
    invalidPhone: 'Enter a valid Malaysia phone number',
  },

  bm: {
    pageTitle: 'Hantar Aduan',
    pageSubtitle: 'Laporkan isu kepada pasukan sokongan',
    eyebrow: 'Permintaan sokongan',
    heroTitle: 'Hantar Aduan',
    heroSubtitle: 'Buat permintaan sokongan dengan langkah yang mudah dan jelas.',

    stepPrefix: 'Langkah',
    stepOf: 'daripada',
    step1: 'Kategori',
    step2: 'Butiran',
    step3: 'Foto Pelawat',
    step4: 'Semakan',

    selectCategory: 'Pilih kategori aduan',
    selectCategoryDesc:
      'Pilih kategori yang paling sesuai supaya kami boleh salurkan kepada pasukan yang betul.',

    detailsTitle: 'Terangkan isu anda',
    detailsDesc: 'Berikan maklumat yang mencukupi supaya pasukan sokongan boleh memahami isu tersebut.',

    visitorPhotoTitle: 'Foto pelawat',
    visitorPhotoDesc:
      'Ambil gambar pelawat menggunakan kamera kiosk. Foto diperlukan sebelum semakan. Muat naik fail tidak dibenarkan untuk keselamatan kiosk awam.',

    reviewTitle: 'Semak aduan',
    reviewDesc: 'Sila semak maklumat sebelum menghantar aduan.',

    name: 'Nama',
    phone: 'Nombor Telefon',
    area: 'Kawasan',
    otherArea: 'Kawasan Lain',
    selectArea: 'Pilih kawasan',
    subject: 'Subjek',
    priority: 'Keutamaan',
    description: 'Penerangan',
    category: 'Kategori',

    namePlaceholder: 'Contoh: Martin',
    phonePlaceholder: '161234567',
    otherAreaPlaceholder: 'Sila masukkan kawasan anda',
    subjectPlaceholder: 'Contoh: Penghawa dingin tidak berfungsi',
    descriptionPlaceholder:
      'Sila terangkan apa yang berlaku, bila ia berlaku, dan maklumat penting yang berkaitan.',

    continue: 'Teruskan',
    back: 'Kembali',
    review: 'Semakan',
    submit: 'Hantar Aduan',

    openCamera: 'Buka Kamera',
    capturePhoto: 'Ambil Gambar',
    retakePhoto: 'Ambil Semula',
    capturingIn: 'Mengambil dalam',
    getReady: 'Bersedia',
    cameraPreview: 'Paparan kamera',
    cameraPreviewDesc: 'Kiosk akan membuka kamera terus di ruangan ini.',
    cameraNotSupported: 'Kamera tidak disokong pada pelayar atau peranti ini.',
    cameraPermission: 'Kamera tidak dapat dibuka. Sila semak kebenaran kamera.',

    visitorPhoto: 'Foto Pelawat',
    required: 'Diperlukan',
    captured: 'Diambil',
    notCaptured: 'Belum diambil',
    notSelected: 'Belum dipilih',
    notEntered: 'Belum diisi',

    tips: 'Tips',
    ticketGuide: 'Panduan Aduan',
    ticketGuideDesc:
      'Pilih kategori yang betul dan berikan maklumat yang jelas supaya pasukan boleh bertindak dengan lebih cepat.',
    currentSelection: 'Pilihan Semasa',
    tipLocation: 'Sertakan lokasi jika berkaitan.',
    tipPhoto: 'Ambil foto pelawat untuk memudahkan tindakan susulan.',
    tipNoUpload: 'Muat naik fail tidak dibenarkan pada kiosk awam.',

    phoneRequired: 'Nombor telefon diperlukan',
    invalidPhone: 'Masukkan nombor telefon Malaysia yang sah',
  },
}

export default function SubmitTicketPage() {
  useKioskAutoHome()

  const navigate = useNavigate()
  const { lang } = useLang()
  const t = CONTENT[lang] || CONTENT.en

  const videoRef = useRef(null)
  const canvasRef = useRef(null)

  const [step, setStep] = useState(1)
  const [category, setCategory] = useState('')
  const [visitorName, setVisitorName] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [area, setArea] = useState('')
  const [otherArea, setOtherArea] = useState('')
  const [subject, setSubject] = useState('')
  const [priority, setPriority] = useState('Medium')
  const [description, setDescription] = useState('')
  const [visitorPhoto, setVisitorPhoto] = useState(null)
  const [cameraStream, setCameraStream] = useState(null)
  const [cameraError, setCameraError] = useState('')
  const [captureCountdown, setCaptureCountdown] = useState(0)

  const stepLabels = [t.step1, t.step2, t.step3, t.step4]

  const selectedCategory = CATEGORIES.find((item) => item.key === category)
  const selectedPriority = PRIORITIES.find((item) => item.key === priority)

  const selectedArea = area === 'Other' ? otherArea.trim() : area
  const isPhoneValid = /^1[0-9]{8,9}$/.test(phone) || /^0[0-9]{9,10}$/.test(phone)

  const canContinueStep1 = Boolean(category)
  const canContinueStep2 = Boolean(
    visitorName.trim() &&
      isPhoneValid &&
      selectedArea &&
      subject.trim() &&
      description.trim(),
  )
  const canContinueStep3 = Boolean(visitorPhoto)

  const getCategoryLabel = (item) => {
    if (!item) return ''
    return lang === 'bm' ? item.bm : item.en
  }

  const getCategoryDesc = (item) => {
    if (!item) return ''
    return lang === 'bm' ? item.descBm : item.descEn
  }

  const getPriorityLabel = (item) => {
    if (!item) return ''
    return lang === 'bm' ? item.bm : item.en
  }

  const getAreaLabel = (item) => {
    if (item === 'Other') return lang === 'bm' ? 'Lain-lain' : 'Other'
    return item
  }

  const goToStep = (targetStep) => {
    if (targetStep === 1) setStep(1)
    if (targetStep === 2 && canContinueStep1) setStep(2)
    if (targetStep === 3 && canContinueStep2) setStep(3)
    if (targetStep === 4 && canContinueStep2 && canContinueStep3) setStep(4)
  }

  const handlePhoneChange = (event) => {
    let value = event.target.value.replace(/\D/g, '')

    if (value.startsWith('60')) value = value.slice(2)
    if (value.length > 11) value = value.slice(0, 11)

    setPhone(value)

    if (!value) {
      setPhoneError(t.phoneRequired)
    } else if (!(/^1[0-9]{8,9}$/.test(value) || /^0[0-9]{9,10}$/.test(value))) {
      setPhoneError(t.invalidPhone)
    } else {
      setPhoneError('')
    }
  }

  const getFullPhoneNumber = () => {
    if (!phone) return ''
    if (phone.startsWith('0')) return `+6${phone}`
    return `+60${phone}`
  }

  const stopCamera = (stream = cameraStream) => {
    stream?.getTracks?.().forEach((track) => track.stop())

    if (videoRef.current) {
      videoRef.current.srcObject = null
    }

    setCameraStream(null)
  }

  const startCamera = async () => {
    setCameraError('')

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(t.cameraNotSupported)
      return
    }

    try {
      stopCamera()

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      })

      setCameraStream(stream)

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }
    } catch (error) {
      console.error('Unable to start camera:', error)
      setCameraError(t.cameraPermission)
    }
  }

  const startCaptureCountdown = () => {
    if (!cameraStream || captureCountdown > 0) return

    setCaptureCountdown(3)

    let nextCount = 3

    const timer = window.setInterval(() => {
      nextCount -= 1

      if (nextCount <= 0) {
        window.clearInterval(timer)
        setCaptureCountdown(0)
        captureCurrentPhoto()
        return
      }

      setCaptureCountdown(nextCount)
    }, 1000)
  }

  const captureCurrentPhoto = () => {
    const video = videoRef.current
    const canvas = canvasRef.current

    if (!video || !canvas) return

    const width = video.videoWidth || 1280
    const height = video.videoHeight || 720

    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')

    context.translate(width, 0)
    context.scale(-1, 1)
    context.drawImage(video, 0, 0, width, height)

    canvas.toBlob(
      (blob) => {
        if (!blob) return

        if (visitorPhoto?.preview) {
          URL.revokeObjectURL(visitorPhoto.preview)
        }

        const file = new File([blob], `visitor-photo-${Date.now()}.jpg`, {
          type: 'image/jpeg',
        })

        setVisitorPhoto({
          file,
          preview: URL.createObjectURL(file),
          name: file.name,
        })

        stopCamera()
      },
      'image/jpeg',
      0.92,
    )
  }

  const removePhoto = () => {
    setCaptureCountdown(0)

    if (visitorPhoto?.preview) {
      URL.revokeObjectURL(visitorPhoto.preview)
    }

    setVisitorPhoto(null)
    startCamera()
  }

  useEffect(() => {
    if (step === 3 && !visitorPhoto) {
      startCamera()
    } else {
      stopCamera()
    }

    return () => {
      stopCamera()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  useEffect(() => {
    return () => {
      if (visitorPhoto?.preview) {
        URL.revokeObjectURL(visitorPhoto.preview)
      }

      stopCamera()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!canContinueStep2 || !canContinueStep3) return

    navigate('/thank-you', { replace: true })
  }

  return (
    <main className="kiosk-page">
      <div className="app-container kiosk-container submit-ticket-page">
        <KioskHeader title={t.pageTitle} subtitle={t.pageSubtitle} />

        <div className="submit-ticket-hero">
          <div>
            <span className="submit-ticket-hero__eyebrow">
              <Ticket size={16} /> {t.eyebrow}
            </span>

            <h1 className="submit-ticket-hero__title">{t.heroTitle}</h1>

            <p className="submit-ticket-hero__subtitle">{t.heroSubtitle}</p>
          </div>

          <div className="submit-ticket-hero__status">
            <span>
              {t.stepPrefix} {step} {t.stepOf} 4
            </span>
            <strong>{stepLabels[step - 1]}</strong>
          </div>
        </div>

        <div className="submit-ticket-layout">
          <form className="submit-ticket-main" onSubmit={handleSubmit}>
            <div className="submit-ticket-steps">
              {stepLabels.map((label, index) => {
                const number = index + 1

                return (
                  <button
                    key={label}
                    type="button"
                    className={`submit-ticket-step ${step === number ? 'is-active' : ''} ${
                      step > number ? 'is-done' : ''
                    }`}
                    onClick={() => goToStep(number)}
                  >
                    <span>{number}</span>
                    <strong>{label}</strong>
                  </button>
                )
              })}
            </div>

            {step === 1 && (
              <section className="submit-ticket-section">
                <div className="submit-ticket-section__header">
                  <h2>{t.selectCategory}</h2>
                  <p>{t.selectCategoryDesc}</p>
                </div>

                <div className="category-select-grid">
                  {CATEGORIES.map((item) => {
                    const Icon = item.icon

                    return (
                      <button
                        key={item.key}
                        type="button"
                        className={`category-select-card ${
                          category === item.key ? 'is-selected' : ''
                        }`}
                        onClick={() => setCategory(item.key)}
                      >
                        <span className="category-select-card__icon">
                          <Icon size={24} />
                        </span>

                        <span>
                          <strong>{getCategoryLabel(item)}</strong>
                          <small>{getCategoryDesc(item)}</small>
                        </span>
                      </button>
                    )
                  })}
                </div>

                <div className="submit-ticket-actions">
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={!canContinueStep1}
                    onClick={() => setStep(2)}
                  >
                    {t.continue} <ArrowRight size={16} />
                  </button>
                </div>
              </section>
            )}

            {step === 2 && (
              <section className="submit-ticket-section">
                <div className="submit-ticket-section__header">
                  <h2>{t.detailsTitle}</h2>
                  <p>{t.detailsDesc}</p>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">{t.name}</label>
                    <input
                      className="form-input"
                      value={visitorName}
                      onChange={(e) => setVisitorName(e.target.value)}
                      placeholder={t.namePlaceholder}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t.phone}</label>
                    <div className="phone-input phone-input--malaysia">
                      <div className="phone-input__prefix">🇲🇾 +60</div>
                      <input
                        type="tel"
                        inputMode="numeric"
                        className={`form-input phone-input__number ${phoneError ? 'is-error' : ''}`}
                        value={phone}
                        onChange={handlePhoneChange}
                        onBlur={handlePhoneChange}
                        placeholder={t.phonePlaceholder}
                      />
                    </div>
                    {phoneError && <div className="form-error">{phoneError}</div>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t.area}</label>
                    <select
                      className="form-input"
                      value={area}
                      onChange={(e) => {
                        setArea(e.target.value)
                        if (e.target.value !== 'Other') setOtherArea('')
                      }}
                    >
                      <option value="">{t.selectArea}</option>
                      {AREAS.map((item) => (
                        <option key={item} value={item}>
                          {getAreaLabel(item)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {area === 'Other' && (
                    <div className="form-group">
                      <label className="form-label">{t.otherArea}</label>
                      <input
                        className="form-input"
                        value={otherArea}
                        onChange={(e) => setOtherArea(e.target.value)}
                        placeholder={t.otherAreaPlaceholder}
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">{t.subject}</label>
                    <input
                      className="form-input"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder={t.subjectPlaceholder}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t.priority}</label>
                    <div className="priority-pill-group">
                      {PRIORITIES.map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          className={`priority-pill ${priority === item.key ? 'is-selected' : ''}`}
                          onClick={() => setPriority(item.key)}
                        >
                          {getPriorityLabel(item)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="form-group form-group--full">
                    <label className="form-label">{t.description}</label>
                    <textarea
                      className="form-textarea"
                      rows={7}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t.descriptionPlaceholder}
                    />
                  </div>
                </div>

                <div className="submit-ticket-actions">
                  <button type="button" className="btn btn--ghost" onClick={() => setStep(1)}>
                    <ArrowLeft size={16} /> {t.back}
                  </button>

                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={!canContinueStep2}
                    onClick={() => setStep(3)}
                  >
                    {t.continue} <ArrowRight size={16} />
                  </button>
                </div>
              </section>
            )}

            {step === 3 && (
              <section className="submit-ticket-section">
                <div className="submit-ticket-section__header">
                  <h2>{t.visitorPhotoTitle}</h2>
                  <p>{t.visitorPhotoDesc}</p>
                </div>

                <div className="visitor-photo-box">
                  {visitorPhoto ? (
                    <div className="visitor-photo-preview">
                      <img src={visitorPhoto.preview} alt={t.visitorPhoto} />

                      <button
                        type="button"
                        className="visitor-photo-remove"
                        onClick={removePhoto}
                        aria-label={t.retakePhoto}
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ) : (
                    <div className="visitor-camera-preview">
                      <video
                        ref={videoRef}
                        className="visitor-camera-video"
                        autoPlay
                        muted
                        playsInline
                      />

                      {captureCountdown > 0 && (
                        <div className="visitor-camera-countdown">
                          <span>{captureCountdown}</span>
                          <strong>{t.getReady}</strong>
                        </div>
                      )}

                      {!cameraStream && (
                        <div className="visitor-photo-empty">
                          <UserFocus size={42} />
                          <strong>{t.cameraPreview}</strong>
                          <span>{cameraError || t.cameraPreviewDesc}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <canvas ref={canvasRef} hidden />

                  <div className="visitor-photo-controls">
                    {visitorPhoto ? (
                      <button
                        type="button"
                        className="btn btn--primary visitor-photo-button"
                        onClick={removePhoto}
                      >
                        <Camera size={18} />
                        {t.retakePhoto}
                      </button>
                    ) : cameraStream ? (
                      <button
                        type="button"
                        className="btn btn--primary visitor-photo-button"
                        onClick={startCaptureCountdown}
                        disabled={captureCountdown > 0}
                      >
                        <Camera size={18} />
                        {captureCountdown > 0
                          ? `${t.capturingIn} ${captureCountdown}`
                          : t.capturePhoto}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--primary visitor-photo-button"
                        onClick={startCamera}
                      >
                        <Camera size={18} />
                        {t.openCamera}
                      </button>
                    )}
                  </div>
                </div>

                <div className="submit-ticket-actions">
                  <button type="button" className="btn btn--ghost" onClick={() => setStep(2)}>
                    <ArrowLeft size={16} /> {t.back}
                  </button>

                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={!canContinueStep3}
                    onClick={() => setStep(4)}
                  >
                    {t.review} <ArrowRight size={16} />
                  </button>
                </div>
              </section>
            )}

            {step === 4 && (
              <section className="submit-ticket-section">
                <div className="submit-ticket-section__header">
                  <h2>{t.reviewTitle}</h2>
                  <p>{t.reviewDesc}</p>
                </div>

                <div className="review-card">
                  <div className="review-card__row">
                    <span>{t.name}</span>
                    <strong>{visitorName || '-'}</strong>
                  </div>

                  <div className="review-card__row">
                    <span>{t.phone}</span>
                    <strong>{getFullPhoneNumber() || '-'}</strong>
                  </div>

                  <div className="review-card__row">
                    <span>{t.area}</span>
                    <strong>{selectedArea || '-'}</strong>
                  </div>

                  <div className="review-card__row">
                    <span>{t.category}</span>
                    <strong>{getCategoryLabel(selectedCategory) || '-'}</strong>
                  </div>

                  <div className="review-card__row">
                    <span>{t.subject}</span>
                    <strong>{subject || '-'}</strong>
                  </div>

                  <div className="review-card__row">
                    <span>{t.priority}</span>
                    <strong>{getPriorityLabel(selectedPriority) || priority}</strong>
                  </div>

                  <div className="review-card__row">
                    <span>{t.visitorPhoto}</span>
                    <strong>{visitorPhoto ? t.captured : t.notCaptured}</strong>
                  </div>

                  {visitorPhoto && (
                    <div className="review-card__photo">
                      <img src={visitorPhoto.preview} alt={t.visitorPhoto} />
                    </div>
                  )}

                  <div className="review-card__description">
                    <span>{t.description}</span>
                    <p>{description || '-'}</p>
                  </div>
                </div>

                <div className="submit-ticket-actions">
                  <button type="button" className="btn btn--ghost" onClick={() => setStep(3)}>
                    <ArrowLeft size={16} /> {t.back}
                  </button>

                  <button type="submit" className="btn btn--primary">
                    <CheckCircle size={16} /> {t.submit}
                  </button>
                </div>
              </section>
            )}
          </form>

          <aside className="submit-ticket-aside">
            <div className="aside-card aside-card--guide">
              <div className="aside-card__icon">
                <Info size={22} />
              </div>
              <h3>{t.ticketGuide}</h3>
              <p>{t.ticketGuideDesc}</p>
            </div>

            <div className="aside-card">
              <h3>{t.currentSelection}</h3>

              <div className="aside-summary">
                <span>{t.category}</span>
                <strong>{getCategoryLabel(selectedCategory) || t.notSelected}</strong>
              </div>

              <div className="aside-summary">
                <span>{t.name}</span>
                <strong>{visitorName || t.notEntered}</strong>
              </div>

              <div className="aside-summary">
                <span>{t.area}</span>
                <strong>{selectedArea || t.notSelected}</strong>
              </div>

              <div className="aside-summary">
                <span>{t.priority}</span>
                <strong>{getPriorityLabel(selectedPriority)}</strong>
              </div>

              <div className="aside-summary">
                <span>{t.visitorPhoto}</span>
                <strong>{visitorPhoto ? t.captured : t.required}</strong>
              </div>
            </div>

            <div className="aside-card">
              <h3>{t.tips}</h3>
              <div className="tip-list">
                <div>
                  <MapPin size={18} /> {t.tipLocation}
                </div>
                <div>
                  <Camera size={18} /> {t.tipPhoto}
                </div>
                <div>
                  <WarningCircle size={18} /> {t.tipNoUpload}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </main>
  )
}